import React, { FC, useState, useCallback, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@chakra-ui/react'
import { Formik, Form, Field, FormikProps } from 'formik'
import { Dispatch } from 'src/store'
import { ConfigVersionBadge } from 'src/components/ConfigVersionBadge'
import ScheduleList from 'src/translators/form/schedule'
import ScheduleGroupList from 'src/translators/form/schedule-group'
import PoolList from 'src/translators/form/pool'
import JsonEditor from 'src/translators/json-editor'
import Consul from 'src/translators/form/consul'
import {
  Box,
  Column,
  Row,
  Text,
  ButtonLink,
  Card,
  CardContent,
  Chip,
  FormRow,
  Placeholders,
  Spinner,
  ChevronLeftIcon,
} from 'src/ui'
import {
  TextField as FormikTextField,
  JsonEditor as FormikJsonEditor,
  Select as FormikSelect,
  Checkbox as FormikCheckbox,
} from 'src/ui/formik'
import {
  TranslatorConfigSchema,
  emptyConfig,
  normalizeToFormikState,
  normalizeToApiBody,
} from 'src/translators/form/utils'
import {
  STATUS,
  getImageVersions,
  getImageOptions,
  createTranslatorEnvironmentConfig,
  getTranslatorConfig,
  getLatestTranslatorConfigForEnvironment,
} from 'src/utils/api'
import { upgradeScheduleToV2 } from 'src/api/configurator'
import { getAuthUsername } from 'src/utils/auth'
import Secret from './secret'
import { omit } from 'lodash'

const DEPLOYMENT_TOOLTIPS = {
  single_deployment:
    'When enabled, this config deploys a single worker container only. When disabled, this config deploys one worker container and two commander containers. Single deployment should be used in most cases. Commanders are mainly useful when phone number lookups are frequent, or user information is not bulk loaded.',
  worker_memory_mb:
    'The worker memory setting in Mi. The deployer applies this value as both the Kubernetes memory request and memory limit.',
  worker_cpu_request_mi_cpu:
    'The worker CPU request in millicpu. This is the primary CPU tuning option.',
  worker_cpu_max_mi_cpu:
    'Optional worker CPU max in millicpu. If omitted, the deployer applies a CPU limit of max(1000 millicpu, commander_cpu_request_mi_cpu), where 1000 millicpu is the adapter-type default limit.',
  commander_memory_mb:
    'The commander memory setting in Mi. The deployer applies this value as both the Kubernetes memory request and memory limit.',
  commander_cpu_request_mi_cpu:
    'The commander CPU request in millicpu. This is the primary CPU tuning option.',
  commander_cpu_max_mi_cpu:
    'Optional commander CPU max in millicpu. If omitted, the deployer applies a CPU limit of max(1000 millicpu, commander_cpu_request_mi_cpu), where 1000 millicpu is the adapter-type default limit.',
}

const EnvironmentChip: FC<{ environment: string }> = ({ environment }) => (
  <Chip color={environment === 'prod' ? 'blue' : 'yellow'} label={environment.toUpperCase()} />
)

const TitleRow = ({ children }) => (
  <Row
    alignItems="center"
    fontWeight="bold"
    bg="#eee"
    borderRadius={3}
    height="3.5rem"
    pr={2}
    pl={3}
    mb={3}
  >
    {children}
  </Row>
)

type TranslatorFormProps = ReturnType<typeof mapDispatch> & {
  // url params from router
  slug: string
  // others
  translators: Translator[]
}

function TranslatorForm({
  errorNotification,
  slug,
  successNotification,
  translators,
}: // ...props
TranslatorFormProps) {
  const { environment, translator_id, copy_id } = useParams()
  const navigate = useNavigate()

  const [status, setStatus] = useState(STATUS.loading)
  const [isEditingJson, setIsEditingJson] = useState(false)
  const [isSwitchingModes, setIsSwitchingModes] = useState(false)
  const [pendingJsonChanges, setPendingJsonChanges] = useState(null)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [translator, setTranslator] = useState<Translator>(() =>
    translators.find((t) => t.id === translator_id)
  )
  const formikSetValuesRef = useRef<((values: any) => void) | null>(null)
  const [translatorConfig, setTranslatorConfig] = useState<TranslatorConfig>(null)
  const [imageOptions, setImageOptions] = useState<string[]>([])
  const [imageVersions, setImageVersions] = useState<TranslatorImage[]>([])

  // check for 'copy' param and load config to copy 'by id' instead of 'latest-config' if so
  const idToCopy = copy_id ?? null

  const loadImages = useCallback(async () => {
    let [err, response] = await getImageOptions()

    if (err) {
      return console.error('failed to load images')
    }

    setImageOptions(response.data.data.sort())
  }, [])

  const loadImageVersions = useCallback(async (name: string) => {
    let [err, response] = await getImageVersions({ name })

    if (err) {
      return console.error('failed to load images')
    }

    setImageVersions(response.data.data)
  }, [])

  const setLoadedConfig = useCallback(
    (config: TranslatorConfig) => {
      if (config.adapter_module) {
        loadImageVersions(config.adapter_module)
      }

      setStatus(STATUS.loaded)
      setTranslatorConfig(config)
    },
    [loadImageVersions]
  )

  const loadLatestConfig = useCallback(async () => {
    let [err, response] = await getLatestTranslatorConfigForEnvironment({
      environment,
      translator_id,
    })

    if (err) {
      if (err.response.status === 404) {
        setStatus(STATUS.loaded)
        setTranslatorConfig(emptyConfig)
        return
      }

      return console.error('failed to load latest config')
    }

    setLoadedConfig(response.data.data)
  }, [environment, setLoadedConfig, translator_id])

  const loadConfigById = useCallback(
    async ({ id }) => {
      let [err, response] = await getTranslatorConfig({ id })

      if (err) {
        errorNotification({
          message: (
            <Column>
              <Box>
                Failed to find config <em>{id}</em>
              </Box>
              <Box>Initialized empty config</Box>
            </Column>
          ),
        })

        setLoadedConfig(emptyConfig)
        return console.error('failed to load latest config')
      }

      setLoadedConfig(response.data.data)
    },
    [errorNotification, setLoadedConfig]
  )

  const handleAuthAdapterChange = useCallback(
    (item) => {
      if (!item || !item.value) return console.warn('cannot fetch images for auth adapter', item)

      loadImageVersions(item.value)
    },
    [loadImageVersions]
  )

  const submitForm = useCallback(
    async (values) => {
      values.revision_comment = values.new_revision_comment
      values = omit(values, ['new_revision_comment'])

      let config = normalizeToApiBody(values)
      let [err] = await createTranslatorEnvironmentConfig({
        translator_id,
        environment,
        config: { ...config, is_prototype_request: config.prototype, user: getAuthUsername() },
      })

      if (err) {
        console.error('failed to save config', err.response.data)

        await errorNotification({
          message: 'Failed to save config',
        })

        return
      }

      await successNotification({
        message: 'Updated translator environment config',
      })

      navigate(`/translators/companies/${slug}`)
    },
    [environment, errorNotification, navigate, slug, successNotification, translator_id]
  )

  const handleUpgrade = useCallback(async () => {
    if (!translatorConfig?.id) {
      await errorNotification({
        message: 'Cannot upgrade: Configuration ID not found',
      })
      return
    }

    setIsUpgrading(true)

    try {
      const [err, response] = await upgradeScheduleToV2({ configId: translatorConfig.id })

      if (err) {
        throw err
      }

      const upgradedConfig = response.data.data.translator_config

      // Update the translator config with the migrated version
      const updatedTranslatorConfig = {
        ...translatorConfig,
        translator_config: upgradedConfig,
      }

      setTranslatorConfig(updatedTranslatorConfig)

      // Update Formik's form values if the ref is available
      if (formikSetValuesRef.current) {
        formikSetValuesRef.current(normalizeToFormikState(updatedTranslatorConfig))
      }

      await successNotification({
        message: 'Configuration upgraded to Version 2. Remember to save your changes.',
      })
    } catch (err: any) {
      await errorNotification({
        message: err?.response?.data?.message || err?.message || 'Failed to upgrade configuration',
      })
    } finally {
      setIsUpgrading(false)
    }
  }, [errorNotification, successNotification, translatorConfig])

  useEffect(() => {
    if (idToCopy) {
      loadConfigById({ id: idToCopy })
    } else {
      loadLatestConfig()
    }

    loadImages()
  }, [idToCopy, loadConfigById, loadImages, loadLatestConfig])

  useEffect(() => {
    if (!translator) {
      let loadedTranslator = translators.find((t) => t.id === translator_id)
      if (!loadedTranslator) {
        return console.error('failed to find translator in list')
      }
      setTranslator(loadedTranslator)
    }
  }, [translator, translator_id, translators])

  if (status === STATUS.idle) return null
  if (status === STATUS.loading) return <Placeholders.LoadingState />
  if (status === STATUS.failed) return <Placeholders.FailedState />

  if (!translator || !translatorConfig) return null

  return (
    <>
      <Row alignItems="center" mb={4}>
        <Box>
          <ButtonLink
            color="default"
            leftIcon={<ChevronLeftIcon />}
            to={`/translators/companies/${slug}`}
          >
            Back to List
          </ButtonLink>
        </Box>
      </Row>

      <Formik
        initialValues={normalizeToFormikState(translatorConfig)}
        validationSchema={TranslatorConfigSchema}
        onSubmit={submitForm}
        innerRef={(formik) => {
          if (formik) {
            formikSetValuesRef.current = formik.setValues
          }
        }}
      >
        {(formikProps: FormikProps<any>) => {
          if (Object.keys(formikProps.errors).length > 0) {
            console.log('validation errors', formikProps.errors)
          }

          return (
            <Form>
              <Card>
                <CardContent>
                  <Row
                    justifyContent="space-between"
                    pt={1}
                    pb={3}
                    mb={4}
                    borderBottom="1px solid #00000015"
                  >
                    <Row alignItems="center">
                      <Box pr={3}>
                        <EnvironmentChip environment={environment} />
                      </Box>

                      <Box fontWeight="bold" pr={3}>
                        {translator.name}
                      </Box>

                      <ConfigVersionBadge
                        config={formikProps.values}
                        showUpgradeButton={true}
                        onUpgrade={handleUpgrade}
                        isUpgrading={isUpgrading}
                      />
                    </Row>

                    <Row alignItems="center">
                      {formikProps.submitCount > 0 &&
                        Object.keys(formikProps.errors).length > 0 && (
                          <Box color="#f44336" fontSize="sm" px={3}>
                            Please fix form errors
                          </Box>
                        )}

                      {isSwitchingModes && (
                        <Row px={3} alignItems="center">
                          <Spinner speed="0.65s" size="md" />
                        </Row>
                      )}

                      <Button
                        size="sm"
                        mr={3}
                        onClick={() => {
                          setIsSwitchingModes(true)
                          // setTimeout with 0 delay will clear the stack
                          // this forces a render with isSwitchingModes=true
                          // without this, the setStates get batched and we never
                          // see the spinner due to blocking
                          setTimeout(() => {
                            if (isEditingJson && pendingJsonChanges) {
                              formikProps.setValues(normalizeToFormikState(pendingJsonChanges))

                              setPendingJsonChanges(null)
                            }

                            setIsEditingJson(!isEditingJson)
                            setIsSwitchingModes(false)
                          })
                        }}
                      >
                        {isEditingJson ? 'Show Form' : 'Show JSON'}
                      </Button>

                      <Button
                        size="sm"
                        colorScheme="primary"
                        isDisabled={formikProps.isSubmitting}
                        isLoading={formikProps.isSubmitting && formikProps.values.prototype}
                        mr={3}
                        onClick={() => {
                          if (pendingJsonChanges) {
                            formikProps.setSubmitting(true)

                            submitForm(
                              normalizeToFormikState({
                                ...pendingJsonChanges,
                                prototype: true,
                              })
                            )
                          } else {
                            formikProps.setFieldValue('prototype', true, false)
                            formikProps.submitForm()
                          }
                        }}
                      >
                        Save
                      </Button>

                      <Button
                        size="sm"
                        colorScheme="primary"
                        isDisabled={formikProps.isSubmitting}
                        isLoading={formikProps.isSubmitting && !formikProps.values.prototype}
                        onClick={() => {
                          if (pendingJsonChanges) {
                            formikProps.setSubmitting(true)
                            submitForm(
                              normalizeToFormikState({
                                ...pendingJsonChanges,
                                prototype: false,
                              })
                            )
                          } else {
                            formikProps.setFieldValue('prototype', false, false)
                            formikProps.submitForm()
                          }
                        }}
                      >
                        Save &amp; Deploy
                      </Button>
                    </Row>
                  </Row>

                  {isEditingJson ? (
                    <Box>
                      <JsonEditor
                        value={normalizeToApiBody(formikProps.values)}
                        height="80vh"
                        onChange={(value) => setPendingJsonChanges(value)}
                      />
                    </Box>
                  ) : (
                    <Column>
                      <Column width="100%" borderRadius={3}>
                        <TitleRow>Config History</TitleRow>

                        <FormRow>
                          <Box fontSize="sm" color="gray.500">
                            <Text fontWeight="semibold">Previous Revision Comment</Text>
                            <Text mt={2} ml={3}>
                              {formikProps.values.revision_comment || 'null'}
                            </Text>
                          </Box>
                          <Field
                            name="new_revision_comment"
                            label="New Revision Comment"
                            component={FormikTextField}
                          />
                        </FormRow>
                      </Column>

                      <Column width="100%" borderRadius={3} mb={3}>
                        <TitleRow>Adapter</TitleRow>

                        <FormRow>
                          <Field
                            id="adapter.adapter_module"
                            name="adapter.adapter_module"
                            label="Adapter Module"
                            component={FormikSelect}
                            selectProps={{ isClearable: false }}
                            options={imageOptions.map((image) => ({
                              value: image,
                              label: image,
                            }))}
                            onChange={handleAuthAdapterChange}
                          />
                          <Field
                            id="adapter.adapter_version"
                            name="adapter.adapter_version"
                            label="Adapter Version"
                            component={FormikSelect}
                            selectProps={{ isClearable: false, sortOptions: false }}
                            options={[
                              { value: 'latest', label: 'latest' },
                              ...imageVersions.map((image) => ({
                                value: image.version,
                                label:
                                  `${image.commit_sha.substring(0, 7)} - ` +
                                  `${image.commit_message.substring(0, 32)}`,
                              })),
                            ]}
                          />
                        </FormRow>
                      </Column>

                      <Column width="100%" borderRadius={3} mb={5}>
                        <TitleRow>Deployment</TitleRow>

                        <FormRow>
                          <Field
                            name="single_deployment"
                            label="Single Deployment"
                            tooltip={DEPLOYMENT_TOOLTIPS.single_deployment}
                            component={FormikCheckbox}
                          />
                          <Box>&nbsp;</Box>
                        </FormRow>

                        <FormRow>
                          <Field
                            name="worker_memory_mb"
                            label="Worker Memory (Mi)"
                            tooltip={DEPLOYMENT_TOOLTIPS.worker_memory_mb}
                            type="number"
                            component={FormikTextField}
                          />
                          <Field
                            name="worker_cpu_request_mi_cpu"
                            label="Worker CPU Request (millicpu)"
                            tooltip={DEPLOYMENT_TOOLTIPS.worker_cpu_request_mi_cpu}
                            type="number"
                            component={FormikTextField}
                          />
                          <Field
                            name="worker_cpu_max_mi_cpu"
                            label="Worker CPU Max (millicpu)"
                            tooltip={DEPLOYMENT_TOOLTIPS.worker_cpu_max_mi_cpu}
                            type="number"
                            component={FormikTextField}
                          />
                        </FormRow>

                        {!formikProps.values.single_deployment && (
                          <FormRow>
                            <Field
                              name="commander_memory_mb"
                              label="Commander Memory (Mi)"
                              tooltip={DEPLOYMENT_TOOLTIPS.commander_memory_mb}
                              type="number"
                              component={FormikTextField}
                            />
                            <Field
                              name="commander_cpu_request_mi_cpu"
                              label="Commander CPU Request (millicpu)"
                              tooltip={DEPLOYMENT_TOOLTIPS.commander_cpu_request_mi_cpu}
                              type="number"
                              component={FormikTextField}
                            />
                            <Field
                              name="commander_cpu_max_mi_cpu"
                              label="Commander CPU Max (millicpu)"
                              tooltip={DEPLOYMENT_TOOLTIPS.commander_cpu_max_mi_cpu}
                              type="number"
                              component={FormikTextField}
                            />
                          </FormRow>
                        )}
                      </Column>

                      <Column width="100%" borderRadius={3} mb={5}>
                        <TitleRow>Auth</TitleRow>

                        <FormRow>
                          <Field
                            name="auth.adapter"
                            label="Auth Adapter"
                            component={FormikTextField}
                          />

                          <Box>&nbsp;</Box>
                        </FormRow>

                        <Column>
                          <Field
                            name="auth.adapter_config"
                            label="Auth Adapter Config"
                            component={FormikJsonEditor}
                            height={180}
                          />
                        </Column>
                      </Column>

                      <Column width="100%" borderRadius={3} mb={5}>
                        <TitleRow>Global Config</TitleRow>
                        <Box>
                          <Field name="global" component={FormikJsonEditor} height={350} />
                        </Box>
                      </Column>

                      <Column width="100%" borderRadius={3} mb={5}>
                        <TitleRow>Operations</TitleRow>
                        <Box>
                          <Field name="operations" component={FormikJsonEditor} height={180} />
                        </Box>
                      </Column>

                      <Consul formikBag={formikProps} />

                      {/* Use V2 schedule groups or V1 flat schedules based on explicit version field */}
                      {/* Only V1 configs lack a version field, V2+ are explicitly versioned */}
                      {formikProps.values.version === '2' ? (
                        <ScheduleGroupList
                          formikProps={formikProps}
                          translatorId={translator_id}
                          companySlug={slug}
                          adapterModule={formikProps.values.adapter?.adapter_module}
                        />
                      ) : (
                        <ScheduleList formikProps={formikProps} />
                      )}

                      <PoolList formikProps={formikProps} />
                    </Column>
                  )}
                </CardContent>
              </Card>
              <Box mt={4}>
                <Secret translator={translator} />
              </Box>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

const mapDispatch = ({ notifications }: Dispatch) => ({
  successNotification: notifications.createSuccess,
  errorNotification: notifications.createError,
})

export default connect(null, mapDispatch)(TranslatorForm)
